import BillingMethod from 'billing/models/BillingMethod'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import { action, computed, observable, makeObservable } from 'mobx'
import { Observable } from 'rxjs'
import { fromStream } from 'utils/mobx-util'
import link from 'infra/link'
import { Id } from 'types/definitions'

/**
 * Submission status view model.
 */
export default class SubmissionStatusModalViewModel extends ModalViewModel {
  /**
   * Submission status.
   */
  @observable status = SubmissionStatus.Submitting

  /**
   * If `status` is `Failed`, represents what went wrong.
   */
  @observable error = ''

  /**
   * Streams as Observables.
   *
   * @private
   */
  private readonly $ = {
    job: fromStream(Job.none, this.job$),
  }

  /**
   * Constructor.
   */
  constructor(
    private job$: Observable<Job>,
    private _paymentMethod: () => BillingMethod | null,
    private _copyJob: () => Promise<{ jobId: Id }>,
    private _canBypassBilling: () => boolean,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * The payment method that was used, if any.
   */
  @computed
  get paymentMethod() {
    return this._paymentMethod()
  }

  /**
   * Whether billing can be bypassed.
   */
  @computed
  get canBypassBilling() {
    return this._canBypassBilling()
  }

  /**
   * The job that was submitted.
   */
  @computed
  get job() {
    return this.$.job()
  }

  copyJob = async () => {
    const { jobId } = await this._copyJob()
    return link.job(Id.assert(jobId))
  }

  /**
   * Shows the modal in the submitting state.
   */
  @action.bound
  startSubmission() {
    this.status = SubmissionStatus.Submitting
    this.error = ''
    this.show()
  }

  /**
   * The submission failed.
   * @param error
   */
  @action.bound
  submissionFailed(error: string) {
    this.status = SubmissionStatus.Failed
    this.error = error
  }

  /**
   * The submission was successful.
   */
  @action.bound
  submissionSuccessful() {
    this.status = SubmissionStatus.Success
    this.error = ''
  }
}

/**
 * Submission status.
 */
export const enum SubmissionStatus {
  Submitting,
  Success,
  Failed,
}
